// Bulk PM setup modal launched from an inventory folder's three-dot menu.
// Mirrors the UX of CreateWorkModal: enum-driven view progression, icon-
// prefixed rows, footer Back/Cancel/Save buttons. Step 1 uses an Autocomplete
// to find PM templates; step 2 renders each picked template's schedule as a
// plain stack of input rows (no card wrappers).

import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import TriggerBuilder from '@atom/components/workTemplate/schedule/TriggerBuilder';
import {
  Autocomplete,
  Button,
  Checkbox,
  Icon,
  IconButton,
  List,
  Modal,
  Snackbar,
  Switch,
  TextField,
} from '@atom/mui';
import colors from '@atom/styles/colors';
import type {
  InventoryCategoryTree,
  InventorySchemaItem,
} from '@atom/types/inventory';
import type {
  ScheduleTrigger,
  WorkTemplateSchedule,
} from '@atom/types/workSchedule';
import type { WorkTemplate } from '@atom/types/workTemplate';

import classes from './setUpPMModal.module.css';

const { ListItem, ListItemText } = List;

type SetUpPMModalProps = {
  open: boolean;
  onClose: () => void;
  category: InventoryCategoryTree;
};

type TemplateRow = WorkTemplate & {
  schedule?: WorkTemplateSchedule;
};

type LocalSchedule = {
  templateId: string;
  active: boolean;
  trigger: ScheduleTrigger;
  leadTimeDays: number;
  covers: string[];
  coversWindowDays: number;
};

enum View {
  TEMPLATES,
  SCHEDULES,
}

const styles = {
  modal: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '2rem',
  },
  modalSchedules: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: '2rem',
    minHeight: '50vh',
    maxHeight: '70vh',
  },
  icon: {
    marginRight: '2rem',
    color: colors.neutral.gray,
  },
  back: {
    marginRight: '1rem',
  },
};

const defaultTrigger = (): ScheduleTrigger => ({
  kind: 'CALENDAR',
  cadence: 'MONTHLY',
  anchorDate: new Date().toISOString().slice(0, 10),
});

const SetUpPMModal = (props: SetUpPMModalProps) => {
  const { open, onClose, category } = props;

  const [view, setView] = useState<View>(View.TEMPLATES);
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [schemas, setSchemas] = useState<InventorySchemaItem[]>([]);
  const [picked, setPicked] = useState<TemplateRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, LocalSchedule>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetch('/api/v1/workTemplates?limit=200')
      .then(r => r.json())
      .then((arr: any[]) => setTemplates(Array.isArray(arr) ? arr : []))
      .catch(() => setTemplates([]));
    fetch('/api/v1/schemas')
      .then(r => r.json())
      .then((arr: any[]) => setSchemas(Array.isArray(arr) ? arr : []))
      .catch(() => setSchemas([]));
  }, [open]);

  const resetState = () => {
    setView(View.TEMPLATES);
    setPicked([]);
    setDrafts({});
    setSaving(false);
  };

  useEffect(() => {
    if (open) resetState();
  }, [open, category?.id]);

  const eligibleTemplates = useMemo(() => {
    const folderSchemaId = category?.schemaId;
    if (!folderSchemaId) return templates;
    return templates.filter(t => !t.schemaId || t.schemaId === folderSchemaId);
  }, [templates, category?.schemaId]);

  const schemaForFolder = useMemo(
    () => schemas.find(s => s.id === category?.schemaId),
    [schemas, category?.schemaId],
  );

  // Whenever the picked set changes, seed/prune drafts accordingly.
  useEffect(() => {
    const pickedIds = new Set(picked.map(t => t.id));
    const nextDrafts: Record<string, LocalSchedule> = {};
    picked.forEach(t => {
      if (drafts[t.id]) {
        // keep existing draft, but prune covers references that are no
        // longer in the picked set.
        nextDrafts[t.id] = {
          ...drafts[t.id],
          covers: drafts[t.id].covers.filter(c => pickedIds.has(c)),
        };
      } else {
        const existing = t.schedule;
        nextDrafts[t.id] = existing
          ? {
              templateId: t.id,
              active: !!existing.active,
              trigger: existing.trigger,
              leadTimeDays: existing.leadTimeDays,
              covers: (existing.supersedes ?? []).filter(c => pickedIds.has(c)),
              coversWindowDays: existing.supersedesWindowDays ?? 7,
            }
          : {
              templateId: t.id,
              active: true,
              trigger: defaultTrigger(),
              leadTimeDays: 7,
              covers: [],
              coversWindowDays: 7,
            };
      }
    });
    setDrafts(nextDrafts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  const patchDraft = (id: string, updates: Partial<LocalSchedule>) => {
    setDrafts({ ...drafts, [id]: { ...drafts[id], ...updates } });
  };

  const toggleCovers = (forId: string, otherId: string) => {
    const d = drafts[forId];
    if (!d) return;
    const next = d.covers.includes(otherId)
      ? d.covers.filter(x => x !== otherId)
      : [...d.covers, otherId];
    patchDraft(forId, { covers: next });
  };

  const handleSave = async () => {
    if (picked.length === 0) return;
    setSaving(true);
    const promises = picked.map(t => {
      const d = drafts[t.id];
      const payload: WorkTemplateSchedule = {
        active: d.active,
        targetCategoryId: category.id,
        trigger: d.trigger,
        leadTimeDays: d.leadTimeDays,
        supersedes: d.covers,
        supersedesWindowDays: d.coversWindowDays,
      };
      return fetch(`/api/v1/work-templates/${t.id}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    });
    const results = await Promise.all(promises);
    setSaving(false);
    const failed = results.filter(r => !r.ok).length;
    const counts = await Promise.all(
      results.map(r =>
        r.ok
          ? r
              .json()
              .then((b: any) => b?.generated ?? 0)
              .catch(() => 0)
          : Promise.resolve(0),
      ),
    );
    const totalGen = counts.reduce((a, b) => a + b, 0);
    if (failed > 0) {
      Snackbar.error({
        message: `Saved ${results.length - failed} schedules; ${failed} failed`,
      });
    } else {
      const genSuffix =
        totalGen > 0
          ? ` — ${totalGen} work order${totalGen === 1 ? '' : 's'} generated`
          : '';
      Snackbar.info({
        message: `${results.length} PM schedule${results.length === 1 ? '' : 's'} saved for ${category.name}${genSuffix}.`,
      });
    }
    window.dispatchEvent(new CustomEvent('notifications:refresh'));
    onClose();
  };

  const otherPicked = (id: string): TemplateRow[] =>
    picked.filter(t => t.id !== id);

  const getTitle = () => {
    if (view === View.SCHEDULES) {
      return (
        <>
          <IconButton
            style={styles.back}
            onClick={() => setView(View.TEMPLATES)}
          >
            <Icon>arrow_back</Icon>
          </IconButton>
          Configure PM schedules
        </>
      );
    }
    return `Set up PM schedules for ${category?.name ?? 'folder'}`;
  };

  const getFooter = () => {
    if (view === View.TEMPLATES) {
      return (
        <div className={classes.footer}>
          <div />
          <div>
            <Button onClick={onClose} style={{ marginRight: '0.5rem' }}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={picked.length === 0}
              onClick={() => setView(View.SCHEDULES)}
            >
              {`Next — Configure ${picked.length} schedule${picked.length === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className={classes.footer}>
        <Button onClick={() => setView(View.TEMPLATES)}>Back</Button>
        <div>
          <Button onClick={onClose} style={{ marginRight: '0.5rem' }}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={saving || picked.length === 0}
            onClick={handleSave}
          >
            {saving
              ? 'Saving…'
              : `Save ${picked.length} schedule${picked.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={getTitle()}
      open={open}
      onCancel={onClose}
      onExited={resetState}
      footer={getFooter()}
      disableEscapeKeyDown={false}
      contentStyle={
        view === View.SCHEDULES ? styles.modalSchedules : styles.modal
      }
    >
      {/* Folder context card — pinned at the top of both views */}
      <div className={clsx(classes.modalRow, classes.folderContext)}>
        <Icon style={styles.icon}>folder</Icon>
        <ListItem disableGutters style={{ margin: 0, padding: 0 }}>
          <ListItemText
            style={{ margin: 0 }}
            primary={category?.name}
            secondary={
              schemaForFolder
                ? `${schemaForFolder.name} · ${category?.path ?? ''}`
                : category?.path ?? ''
            }
          />
        </ListItem>
      </div>

      {view === View.TEMPLATES && (
        <>
          <div className={classes.viewIntro}>
            Search for the PM templates you want to schedule against this
            folder.
            {schemaForFolder && (
              <>
                {' '}Results are filtered to those that match the
                <strong> {schemaForFolder.name} </strong>
                schema.
              </>
            )}
          </div>

          <div className={classes.modalRow}>
            <Icon style={styles.icon}>search</Icon>
            <div className={classes.fillWidth}>
              <Autocomplete
                multiple
                fullWidth
                autoFocus
                popperAutoWidth
                options={eligibleTemplates}
                value={picked}
                onChange={(_e: any, value: any) =>
                  setPicked(Array.isArray(value) ? value : [])
                }
                getOptionLabel={(opt: any) => opt?.name ?? ''}
                placeholder={
                  picked.length === 0
                    ? 'Search PM templates by name…'
                    : 'Add another…'
                }
                noOptionsText="No matching PM templates"
                renderOption={(propOptions: any, option: any) => {
                  const already = !!option.schedule;
                  return (
                    <li {...propOptions}>
                      <ListItemText
                        primary={option.name}
                        secondary={
                          already
                            ? 'Already scheduled — pick to edit'
                            : undefined
                        }
                      />
                      {already && (
                        <Icon style={{ color: colors.brand.blue }}>
                          autorenew
                        </Icon>
                      )}
                    </li>
                  );
                }}
              />
            </div>
          </div>

          {picked.length > 0 && (
            <div className={classes.pickedList}>
              {picked.map(t => (
                <div key={t.id} className={classes.pickedRow}>
                  <Icon
                    style={{ color: colors.brand.blue, marginRight: '0.5rem' }}
                  >
                    autorenew
                  </Icon>
                  <span className={classes.pickedName}>{t.name}</span>
                  <IconButton
                    size="small"
                    onClick={() =>
                      setPicked(picked.filter(x => x.id !== t.id))
                    }
                    tooltip="Remove"
                  >
                    <Icon>close</Icon>
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {view === View.SCHEDULES && (
        <>
          <div className={classes.viewIntro}>
            Configure each PM. All settings save together when you click
            <strong> Save</strong>.
          </div>

          {picked.map((t, idx) => {
            const d = drafts[t.id];
            if (!d) return null;
            const others = otherPicked(t.id);
            return (
              <div
                key={t.id}
                className={clsx(
                  classes.templateSection,
                  idx > 0 && classes.templateSectionDivider,
                )}
              >
                <div className={classes.sectionHeader}>
                  <Icon style={styles.icon}>autorenew</Icon>
                  <ListItem
                    disableGutters
                    style={{ margin: 0, padding: 0, flex: 1 }}
                  >
                    <ListItemText
                      style={{ margin: 0 }}
                      primary={t.name}
                      secondary={
                        d.active
                          ? 'Active — will generate work orders per the trigger below'
                          : 'Inactive — saved but no work generated'
                      }
                    />
                  </ListItem>
                  <Switch
                    checked={d.active}
                    onChange={(_e: any, checked: boolean) =>
                      patchDraft(t.id, { active: checked })
                    }
                    label="Active"
                  />
                </div>

                <div className={classes.modalRow}>
                  <Icon style={styles.icon}>schedule</Icon>
                  <div className={classes.fillWidth}>
                    <TriggerBuilder
                      trigger={d.trigger}
                      schema={schemaForFolder}
                      onChange={(next) => patchDraft(t.id, { trigger: next })}
                    />
                  </div>
                </div>

                <div className={classes.modalRow}>
                  <Icon style={styles.icon}>hourglass_empty</Icon>
                  <TextField
                    label="Lead time (days)"
                    type="number"
                    value={d.leadTimeDays}
                    onChange={(e: any) =>
                      patchDraft(t.id, { leadTimeDays: Number(e.target.value) })
                    }
                  />
                </div>

                {others.length > 0 && (
                  <div className={classes.modalRow}>
                    <Icon style={styles.icon}>layers</Icon>
                    <div className={classes.fillWidth}>
                      <div className={classes.coversLabel}>Covers</div>
                      <div className={classes.coversList}>
                        {others.map(other => (
                          <label
                            key={other.id}
                            className={classes.coversItem}
                          >
                            <Checkbox
                              checked={d.covers.includes(other.id)}
                              onChange={() => toggleCovers(t.id, other.id)}
                            />
                            <span>{other.name}</span>
                          </label>
                        ))}
                      </div>
                      {d.covers.length > 0 && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '0.5rem',
                          }}
                        >
                          <span style={{ fontSize: '0.875rem' }}>
                            Window
                          </span>
                          <div style={{ width: '5rem' }}>
                            <TextField
                              type="number"
                              value={d.coversWindowDays}
                              onChange={(e: any) =>
                                patchDraft(t.id, {
                                  coversWindowDays: Math.max(
                                    0,
                                    Number(e.target.value) || 0,
                                  ),
                                })
                              }
                            />
                          </div>
                          <span style={{ fontSize: '0.875rem' }}>
                            day{d.coversWindowDays === 1 ? '' : 's'} on either
                            side
                          </span>
                        </div>
                      )}
                      <div className={classes.helperText}>
                        When this PM fires within the window of a checked one
                        above, the checked one is skipped — its work is
                        covered here.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </Modal>
  );
};

export default SetUpPMModal;
